<template>
  <app-modal
    ref="modal"
    v-bind="$attrs"
    class="modal-container"
    content-class="modal-medium"
    title="イントラネットFW ゾーン接続 追加"
  >
    <template v-if="isLoaded" v-slot:body="{ cancel }">
      <validation-observer v-slot="{ handleSubmit }" as="div">
        <v-form novalidate @submit.stop.prevent="handleSubmit(submit)">
          <v-card-text class="pa-0">
            <v-container>
              <v-row>
                <v-col>
                  <validation-provider
                    name="sourceZone"
                    v-model="srcZone"
                    label="送信元ゾーン"
                    :rules="{
                      required: true,
                      is_not_eq: {
                        target: '@destinationZone',
                        path: 'zoneId',
                        targetField: '宛先ゾーン',
                      },
                    }"
                    v-slot="{ componentField, errorMessage, meta }"
                  >
                    <app-badge item="送信元ゾーン" :isInfo="false" />
                    <app-select
                      :items="selectZoneOptions"
                      item-title="text"
                      item-value="value"
                      v-bind="componentField"
                      :error-messages="errorMessage"
                      :field-meta="meta"
                    ></app-select>
                  </validation-provider>
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <validation-provider
                    name="destinationZone"
                    v-model="dstZone"
                    label="宛先ゾーン"
                    :rules="{
                      required: true,
                      is_not_eq: {
                        target: '@sourceZone',
                        path: 'zoneId',
                        targetField: '送信元ゾーン',
                      },
                      duplicate_zone_relation: {
                        srcZone: srcZone,
                        relation: zoneRelation.zoneRelationList,
                      },
                    }"
                    v-slot="{ componentField, errorMessage, meta }"
                  >
                    <app-badge item="宛先ゾーン" :isInfo="false" />
                    <app-select
                      :items="selectZoneOptions"
                      item-title="text"
                      item-value="value"
                      v-bind="componentField"
                      :error-messages="errorMessage"
                      :field-meta="meta"
                    ></app-select>
                  </validation-provider>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <v-list-item class="w-100 pa-1">
              <template v-slot:append>
                <v-btn variant="flat" color="primary" type="submit">追加</v-btn>
                <v-btn variant="outlined" color="primary" @click="cancel"
                  >キャンセル
                </v-btn>
              </template>
            </v-list-item>
          </v-card-actions>
        </v-form>
      </validation-observer>
    </template>
  </app-modal>
</template>

<script lang="ts">
import type { PropType } from "vue";
import { defineComponent } from "vue";
import type { ZoneRelation, ZoneRelationPost } from "@/apis/ZoneRelationApi";
import type { Type1ExtraInfo, Type1PrivateInfo } from "@/apis/Type1SiteApi";
import type { Type1ZoneOption } from "@/modals/applianceContractSetting/type1/zoneRelationSetting/InternetType1ZoneRelationRegister.vue";
import { VueFinalModal } from "vue-final-modal";
import AppSelect from "@/components/AppSelect.vue";

export default defineComponent({
  name: "IntranetFwZoneRelationRegister",
  components: { AppSelect },
  props: {
    zoneRelationKeyId: {
      type: String as PropType<ZoneRelation["zoneRelationKeyId"]>,
      required: true,
    },
    zoneRelation: {
      type: Object as PropType<ZoneRelation>,
      required: true,
    },
  },
  data() {
    return {
      // 送信元ゾーン
      srcZone: null as null | Type1ZoneOption,
      // 宛先ゾーン
      dstZone: null as null | Type1ZoneOption,
      // ゾーンオプション
      selectZoneOptions: [] as { value: Type1ZoneOption; text: string }[],
      // 通信完了フラグ
      isLoaded: false,
    };
  },
  async mounted() {
    await this.load();
    this.isLoaded = true;
  },
  methods: {
    /** データ取得 */
    async load() {
      // エクストラゾーン取得
      const extraZone: Type1ExtraInfo["zone"] = (
        await this.$api.type1SiteZone.getType1ExtraList()
      ).zone;
      // プライベートゾーン取得
      const privateZone: Type1PrivateInfo["zoneList"] = (
        await this.$api.type1SiteZone.getType1PrivateList()
      ).zoneList;

      if (privateZone.length > 0) {
        const zoneOptions = privateZone.map((e) => ({
          value: { ...e, type: "PRIVATE" } as Type1ZoneOption,
          text: "プライベートゾーン/" + e.zoneName,
        }));
        // オプションのソート
        this.selectZoneOptions = zoneOptions.sortBy([(e) => e.text, "asc"]);
      }

      if (extraZone !== null) {
        this.selectZoneOptions.unshift({
          value: { ...extraZone, type: "EXTRA" },
          text: "エクストラゾーン/" + extraZone.zoneName,
        });
      }
    },
    /** 追加ボタン押下時の挙動*/
    async submit() {
      await this.$confirm("ゾーン接続を追加します。よろしいですか？");
      const reqData: ZoneRelationPost = {
        srcZoneId: this.srcZone!.zoneId,
        dstZoneId: this.dstZone!.zoneId,
        zoneRelationKeyId: this.zoneRelationKeyId,
      };
      await this.$api.zoneRelation.postZoneRelation(reqData);
      (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
    },
  },
});
</script>
